import { KitButton } from './button';
import { useRef, useState } from 'react';
import type { FieldControlProps } from './form-field';

export interface FileFieldItem {
  id: string;
  name: string;
  sizeLabel: string;
  state: 'uploading' | 'done' | 'error';
  progress?: number;
  error?: string;
  previewUrl?: string;
  // Only an owner-classified retryable refusal may enable retry. A lost upload
  // reply is reconciled by its original operation, never replayed by this kit.
  retryable?: boolean;
}
export function FileField({
  items,
  accept,
  maxBytes,
  multiple = false,
  images = false,
  copy,
  onChoose,
  onRejected,
  onRemove,
  onRetry,
  ...props
}: Omit<FieldControlProps, 'value' | 'onValueChange'> & {
  items: FileFieldItem[];
  accept?: string;
  maxBytes?: number;
  multiple?: boolean;
  images?: boolean;
  copy: {
    choose: string;
    photo: string;
    hint: string;
    uploading: string;
    done: string;
    retry: string;
    retryLabel(name: string): string;
    remove: string;
    removeLabel(name: string): string;
  };
  onChoose(files: File[]): void;
  onRejected(file: File): void;
  onRemove(id: string): void;
  onRetry?(id: string): void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const choose = (selected: File[]) => {
    if (props.disabled) return;
    const files = multiple ? selected : selected.slice(0, 1);
    const accepted = files.filter((file) => {
      if (maxBytes !== undefined && file.size > maxBytes) {
        onRejected(file);
        return false;
      }
      return true;
    });
    if (accepted.length) onChoose(accepted);
  };
  return (
    <div>
      <div
        className="pc-file-drop"
        data-state={over ? 'over' : undefined}
        onDragOver={(event) => {
          event.preventDefault();
          if (!props.disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          choose(Array.from(event.dataTransfer.files));
        }}
      >
        <input
          ref={input}
          id={`${props.id}-input`}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={props.disabled}
          hidden
          onChange={(event) => {
            choose(Array.from(event.currentTarget.files ?? []));
            event.currentTarget.value = '';
          }}
        />
        <KitButton
          {...props}
          type="button"
          disabled={props.disabled}
          onClick={() => input.current?.click()}
        >
          {copy.choose}
        </KitButton>
        {images && (
          <>
            <input
              ref={camera}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              disabled={props.disabled}
              onChange={(event) => {
                choose(Array.from(event.currentTarget.files ?? []));
                event.currentTarget.value = '';
              }}
            />
            <KitButton
              type="button"
              disabled={props.disabled}
              onClick={() => camera.current?.click()}
            >
              {copy.photo}
            </KitButton>
          </>
        )}
        <p>{copy.hint}</p>
      </div>
      <ul className="pc-file-list">
        {items.map((item) => (
          <li key={item.id} className="pc-file-row" data-state={item.state}>
            {item.previewUrl && <img src={item.previewUrl} alt="" />}
            <span className="pc-file-name">{item.name}</span>
            <span className="pc-file-size">{item.sizeLabel}</span>
            {item.state === 'uploading' && (
              <progress
                className="pc-progress"
                aria-label={copy.uploading}
                max={1}
                value={
                  item.progress === undefined ? undefined : Math.max(0, Math.min(1, item.progress))
                }
              />
            )}
            {item.state === 'done' && (
              <span className="pc-file-done" aria-label={copy.done}>
                ✓
              </span>
            )}
            {item.error && <span role="alert">{item.error}</span>}
            {item.state === 'error' && item.retryable && onRetry && (
              <KitButton
                variant="quiet"
                type="button"
                disabled={props.disabled}
                aria-label={copy.retryLabel(item.name)}
                onClick={() => onRetry(item.id)}
              >
                {copy.retry}
              </KitButton>
            )}
            <KitButton
              variant="quiet"
              type="button"
              disabled={props.disabled}
              aria-label={copy.removeLabel(item.name)}
              onClick={() => onRemove(item.id)}
            >
              {copy.remove}
            </KitButton>
          </li>
        ))}
      </ul>
    </div>
  );
}
